/**
 * The URL of an open nomination.
 *
 * The Nomination List keeps the nomination whose detail modal is open in its query string,
 * `?nominationId=`, so the modal is a link: a marketer can send it to a BD, and the BD lands on
 * the list with the modal already open and the review outcome on top. Opening and closing the
 * modal edit that one parameter and leave the rest (`eventId`, `status`) alone, so the list
 * behind the modal is the one the link was taken from.
 *
 * Only the Nomination List does this. Elsewhere (the event detail page's Attendance List) the
 * modal opens in place without touching the URL, though the link's href is still this one, so
 * a new tab or a copied link lands on the list with the nomination open.
 *
 * Here rather than in the page because the coverage config measures only `.ts`.
 */

/** The query parameter naming the open nomination. */
export const NOMINATION_PARAM = 'nominationId';

/** The Nomination List route. */
const LIST_PATH = '/event/nominations';

/**
 * A link to one nomination, opened over its event's list.
 *
 * Scoped to the event when it is known, so the list behind the modal is that event's
 * nominations rather than all of them.
 */
export function nominationHref(nominationId: string, eventId?: string): string {
  const params = new URLSearchParams();
  if (eventId) params.set('eventId', eventId);
  params.set(NOMINATION_PARAM, nominationId);
  return `${LIST_PATH}?${params.toString()}`;
}

/** The edit page for one nomination (EditNominationPage). */
export function editNominationHref(nominationId: string): string {
  return `${LIST_PATH}/${encodeURIComponent(nominationId)}/edit`;
}

/**
 * `params` with the open nomination set to `nominationId`, or removed when it is null.
 *
 * A copy: URLSearchParams is mutable, and the router's instance must not change under it.
 */
export function withOpenNomination(
  params: URLSearchParams,
  nominationId: string | null,
): URLSearchParams {
  const next = new URLSearchParams(params);
  if (nominationId) next.set(NOMINATION_PARAM, nominationId);
  else next.delete(NOMINATION_PARAM);
  return next;
}
